import { Component, type ErrorInfo, type ReactNode } from "react";
import styled from "styled-components";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
};

/**
 * Atrapa errores de cualquier componente del árbol React.
 *
 * Sin este boundary, una excepción no controlada rompe el render entero y
 * la app queda en blanco. Con él, mostramos una pantalla de error amigable
 * con stack trace y opción de copiar el reporte.
 *
 * Tiene que ser una clase porque Error Boundaries no pueden ser hooks
 * (limitación de React).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    copied: false,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    // Log a la consola del browser para debug.
    console.error("ErrorBoundary capturó:", error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      copied: false,
    });
  };

  handleCopyReport = async (): Promise<void> => {
    const { error, errorInfo } = this.state;
    const report = [
      `Speaker Tagger — Error Report`,
      `Date: ${new Date().toISOString()}`,
      `User Agent: ${navigator.userAgent}`,
      ``,
      `Error: ${error?.message ?? "Unknown"}`,
      ``,
      `Stack:`,
      error?.stack ?? "(no stack)",
      ``,
      `Component Stack:`,
      errorInfo?.componentStack ?? "(no component stack)",
    ].join("\n");

    try {
      await navigator.clipboard.writeText(report);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      // Fallback: si el clipboard no está disponible, mostrar el reporte
      // en un prompt para que el usuario pueda copiarlo manualmente.
      window.prompt("Copia manualmente este reporte:", report);
    }
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <Wrapper>
        <Card>
          <Title>Algo salió mal</Title>
          <Message>
            La app encontró un error inesperado. Puedes intentar continuar
            (los datos no cargados se conservan) o reportar el problema.
          </Message>

          <ErrorDetails>
            <Summary>Detalles técnicos</Summary>
            <Pre>
              {this.state.error?.message ?? "Unknown error"}
              {"\n\n"}
              {this.state.error?.stack ?? "(no stack trace)"}
            </Pre>
          </ErrorDetails>

          <Actions>
            <Button onClick={this.handleRetry}>Reintentar</Button>
            <Button $variant="secondary" onClick={this.handleCopyReport}>
              {this.state.copied ? "✓ Copiado" : "Copiar reporte"}
            </Button>
          </Actions>
        </Card>
      </Wrapper>
    );
  }
}

// --- Estilos ---

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #fafafa;
  font-family: system-ui, -apple-system, sans-serif;
`;

const Card = styled.div`
  max-width: 600px;
  width: 100%;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const Title = styled.h1`
  margin: 0 0 0.75rem 0;
  font-size: 1.5rem;
  color: #dc2626;
  font-weight: 600;
`;

const Message = styled.p`
  margin: 0 0 1.5rem 0;
  color: #374151;
  line-height: 1.5;
`;

const ErrorDetails = styled.details`
  margin-bottom: 1.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 0.75rem;
  background: #fafafa;
`;

const Summary = styled.summary`
  cursor: pointer;
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  user-select: none;
`;

const Pre = styled.pre`
  margin: 0.75rem 0 0 0;
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 0.75rem;
  color: #374151;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 240px;
  overflow-y: auto;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const Button = styled.button<{ $variant?: "primary" | "secondary" }>`
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  background: ${({ $variant }) => ($variant === "secondary" ? "white" : "#0066cc")};
  color: ${({ $variant }) => ($variant === "secondary" ? "#1a1a1a" : "white")};
  border: 1px solid ${({ $variant }) => ($variant === "secondary" ? "#cccccc" : "#0066cc")};

  &:hover {
    background: ${({ $variant }) =>
      $variant === "secondary" ? "#fafafa" : "#0052a3"};
  }
`;
